import { createPendingListingSubmission } from "../listing-submission.js";
import {
  buildListingContactRpcArgs,
  buildThreadReplyInsert,
  toAppMessageThread,
} from "../shared/live-contact-mappers.js";
import {
  buildLiveListingInsert,
  toAppListing,
  toAppUser,
  toProfileUpsert,
  toPublicListing,
} from "../shared/live-marketplace-mappers.js";
import { getSupabaseBrowserClient } from "./supabase/client.js";

function requireClient(client) {
  if (!client) throw new Error("Supabase is not configured for this environment.");
  return client;
}

function throwOnError(error, context) {
  if (error) {
    const message = error.message || String(error);
    throw new Error(`${context}: ${message}`);
  }
}

function compactText(value) {
  return String(value || "").trim();
}

function providerUserRowFromAppUser(user = {}) {
  return {
    id: user.database_id || user.id,
    email: user.email,
    phone: user.phone,
    full_name: user.full_name,
    city: user.city,
    state: user.state,
    role: user.role === "paid_provider" ? "enhanced_provider" : "provider",
    status: "active",
    public_profile_fields: {
      parish_id: user.parish_id,
      languages: user.languages || ["English"],
      bio: user.bio,
      avatar_seed: user.avatar_seed,
    },
  };
}

function upsertWindowRecords({ listings = [], users = [], categories = [], parishes = [], replace = false } = {}) {
  if (Array.isArray(globalThis.window?.LISTINGS) && replace) {
    window.LISTINGS = listings;
    if (Array.isArray(window.REQUESTS)) window.REQUESTS = [];
    if (Array.isArray(window.REVIEWS)) window.REVIEWS = [];
  } else if (Array.isArray(globalThis.window?.LISTINGS) && listings.length) {
    const byId = new Map(window.LISTINGS.map((listing) => [listing.id, listing]));
    for (const listing of listings) byId.set(listing.id, listing);
    window.LISTINGS = Array.from(byId.values());
  }
  if (Array.isArray(globalThis.window?.USERS) && replace) {
    window.USERS = users;
  } else if (Array.isArray(globalThis.window?.USERS) && users.length) {
    const byId = new Map(window.USERS.map((user) => [user.id, user]));
    for (const user of users) byId.set(user.id, user);
    window.USERS = Array.from(byId.values());
  }
  if (Array.isArray(globalThis.window?.CATEGORIES) && categories.length) {
    window.CATEGORIES = categories;
  }
  if (Array.isArray(globalThis.window?.PARISHES) && parishes.length) {
    window.PARISHES = parishes;
  }
}

export async function getLiveSession(client) {
  const supabase = requireClient(client);
  const { data, error } = await supabase.auth.getSession();
  throwOnError(error, "Load Supabase Auth session");
  return data.session || null;
}

export function subscribeLiveAuth(client, callback) {
  const supabase = requireClient(client);
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    callback({ event, session });
  });
  return () => data?.subscription?.unsubscribe?.();
}

export async function signInLiveUser(client, { email, password } = {}) {
  const supabase = requireClient(client);
  const { data, error } = await supabase.auth.signInWithPassword({
    email: compactText(email),
    password: String(password || ""),
  });
  throwOnError(error, "Sign in with Supabase Auth");
  const profile = data.user ? await loadLiveUserProfile(supabase, data.user) : null;
  return { ...data, profile };
}

export async function signUpLiveUser(client, { email, password, fullName, roleIntent = "member" } = {}) {
  const supabase = requireClient(client);
  const { data, error } = await supabase.auth.signUp({
    email: compactText(email),
    password: String(password || ""),
    options: {
      data: {
        full_name: compactText(fullName),
        role_intent: roleIntent === "provider" ? "provider" : "member",
      },
    },
  });
  throwOnError(error, "Create Supabase Auth user");

  let profile = null;
  if (data.user && data.session) {
    profile = await saveLiveProfile(supabase, {
      draft: {
        public: {
          full_name: compactText(fullName),
          city: "",
          state: "",
          parish_id: "",
          languages: ["English"],
          bio: "",
        },
        private: { email: compactText(email), phone: "" },
      },
      roleIntent,
    });
  }

  return { ...data, profile };
}

export async function signOutLiveUser(client) {
  const supabase = requireClient(client);
  const { error } = await supabase.auth.signOut();
  throwOnError(error, "Sign out of Supabase Auth");
}

export async function loadLiveReferenceData(client) {
  const supabase = requireClient(client);
  const [{ data: categories, error: categoryError }, { data: parishes, error: parishError }] = await Promise.all([
    supabase
      .from("categories")
      .select("id, slug, name, description, safety_level, license_prompt_required, active, sort_order")
      .eq("active", true)
      .order("sort_order", { ascending: true })
      .order("name", { ascending: true }),
    supabase
      .from("parishes")
      .select("id, slug, name, city, state, region, active")
      .eq("active", true)
      .order("name", { ascending: true }),
  ]);

  throwOnError(categoryError, "Load live categories");
  throwOnError(parishError, "Load live parishes");

  return {
    categories: (categories || []).map((category) => ({
      ...category,
      glyph: compactText(category.name)[0] || "C",
    })),
    parishes: parishes || [],
  };
}

export async function loadLiveUserProfile(client, authUser) {
  const supabase = requireClient(client);
  if (!authUser?.id) return null;

  const { data: user, error: userError } = await supabase
    .from("users")
    .select("*")
    .eq("id", authUser.id)
    .maybeSingle();
  throwOnError(userError, "Load live user profile");
  if (!user) return null;

  const { data: providerProfile, error: providerError } = await supabase
    .from("provider_profiles")
    .select("*")
    .eq("user_id", authUser.id)
    .maybeSingle();
  throwOnError(providerError, "Load live provider profile");

  return toAppUser({
    user,
    providerProfile,
    emailVerified: Boolean(authUser.email_confirmed_at || user.status === "active"),
  });
}

export async function saveLiveProfile(client, { draft, roleIntent } = {}) {
  const supabase = requireClient(client);
  const { data: authData, error: authError } = await supabase.auth.getUser();
  throwOnError(authError, "Load current Supabase user");
  const authUser = authData.user;
  if (!authUser?.id) throw new Error("A Supabase Auth session is required to save profile data.");

  const role = roleIntent === "provider" ? "provider" : "member";
  const userUpsert = toProfileUpsert({ authUser, draft, fallbackRole: role });
  const { data: user, error: userError } = await supabase
    .from("users")
    .upsert(userUpsert, { onConflict: "id" })
    .select("*")
    .single();
  throwOnError(userError, "Persist live profile");

  let providerProfile = null;
  if (role === "provider" || user.role === "provider" || user.role === "enhanced_provider") {
    const { data, error } = await supabase
      .from("provider_profiles")
      .upsert(
        {
          user_id: authUser.id,
          bio: compactText(draft?.public?.bio),
          public_contact_preference: "platform_only",
          onboarding_status: "active",
        },
        { onConflict: "user_id" }
      )
      .select("*")
      .single();
    throwOnError(error, "Persist live provider profile");
    providerProfile = data;
  }

  return toAppUser({ user, providerProfile, emailVerified: Boolean(authUser.email_confirmed_at) });
}

async function ensureProviderProfile(client, currentUser) {
  const supabase = requireClient(client);
  if (currentUser?.provider_profile_id) {
    return {
      id: currentUser.provider_profile_id,
      user_id: currentUser.database_id || currentUser.id,
      bio: currentUser.bio || "",
      public_contact_preference: "platform_only",
    };
  }

  const { data, error } = await supabase
    .from("provider_profiles")
    .upsert(
      {
        user_id: currentUser.database_id || currentUser.id,
        bio: compactText(currentUser.bio),
        public_contact_preference: "platform_only",
        onboarding_status: "active",
      },
      { onConflict: "user_id" }
    )
    .select("*")
    .single();
  throwOnError(error, "Ensure live provider profile");
  return data;
}

export async function submitLiveListing(client, { currentUser, draft, categories, parishes, existingListings } = {}) {
  const supabase = requireClient(client);
  const providerProfile = await ensureProviderProfile(supabase, currentUser);
  const pendingListing = createPendingListingSubmission({
    provider: { ...currentUser, provider_profile_id: providerProfile.id },
    draft,
    categories,
    parishes,
    existingListings,
    now: new Date(),
  });

  if (pendingListing.errors) return pendingListing;

  const insertRow = buildLiveListingInsert({
    providerProfileId: providerProfile.id,
    provider: currentUser,
    pendingListing,
  });
  const { data: listing, error } = await supabase
    .from("listings")
    .insert(insertRow)
    .select("*")
    .single();
  throwOnError(error, "Persist live listing submission");

  return {
    listing: toAppListing({
      listing,
      providerUser: providerUserRowFromAppUser(currentUser),
      providerProfile,
    }),
    databaseListing: listing,
  };
}

export async function loadLivePublicListings(client) {
  const supabase = requireClient(client);
  const { data, error } = await supabase.rpc("get_public_listings");
  throwOnError(error, "Load live public listings");

  const usersById = new Map();
  const listings = [];
  for (const row of data || []) {
    const mapped = toPublicListing(row);
    if (mapped.provider?.id) usersById.set(mapped.provider.id, mapped.provider);
    if (mapped.listing?.id) listings.push(mapped.listing);
  }

  return {
    users: Array.from(usersById.values()),
    listings,
  };
}

export async function loadLivePendingListings(client) {
  const supabase = requireClient(client);
  const { data: listings, error } = await supabase
    .from("listings")
    .select("*")
    .eq("status", "pending_review")
    .order("created_at", { ascending: false });
  throwOnError(error, "Load live pending listings");

  if (!listings?.length) return { listings: [], users: [] };

  const providerProfileIds = [...new Set(listings.map((listing) => listing.provider_id).filter(Boolean))];
  const { data: providerProfiles, error: providerError } = await supabase
    .from("provider_profiles")
    .select("*")
    .in("id", providerProfileIds);
  throwOnError(providerError, "Load pending listing provider profiles");

  const userIds = [...new Set((providerProfiles || []).map((profile) => profile.user_id).filter(Boolean))];
  const { data: users, error: userError } = await supabase
    .from("users")
    .select("*")
    .in("id", userIds);
  throwOnError(userError, "Load pending listing provider users");

  const profileById = new Map((providerProfiles || []).map((profile) => [profile.id, profile]));
  const userById = new Map((users || []).map((user) => [user.id, user]));
  const appUsers = [];
  const appListings = listings.map((listing) => {
    const providerProfile = profileById.get(listing.provider_id);
    const providerUser = providerProfile ? userById.get(providerProfile.user_id) : null;
    const appUser = toAppUser({ user: providerUser, providerProfile, emailVerified: true });
    if (appUser) appUsers.push(appUser);
    return toAppListing({ listing, providerUser, providerProfile });
  });

  return { listings: appListings, users: appUsers };
}

export async function decideLiveListing(client, { listing, action, moderator } = {}) {
  const supabase = requireClient(client);
  const listingId = listing?.database_id || listing?.id;
  if (!listingId) throw new Error("A database listing id is required for live moderation.");

  const { data, error } = await supabase.rpc("moderate_listing_decision", {
    p_listing_id: listingId,
    p_action: action,
    p_reason: moderator?.id ? `moderated by ${moderator.id}` : null,
  });
  throwOnError(error, "Persist audited live listing moderation decision");

  return { listing: data, auditEvent: { action: `listing.${action}`, target_id: listingId } };
}

export async function loadLiveMessageThreads(client, { threadId = null, currentUserId = null } = {}) {
  const supabase = requireClient(client);
  let userId = currentUserId;

  if (!userId) {
    const { data, error } = await supabase.auth.getUser();
    throwOnError(error, "Load current Supabase user for message threads");
    userId = data.user?.id || null;
  }

  const { data, error } = await supabase.rpc("get_my_message_threads", {
    p_thread_id: threadId || null,
  });
  throwOnError(error, "Load live message threads");

  return (data || [])
    .map((row) => toAppMessageThread({ row, currentUserId: userId }))
    .filter(Boolean);
}

export async function startLiveListingContact(client, { listing, currentUser, subject, message, sourcePage } = {}) {
  const supabase = requireClient(client);
  const contactArgs = buildListingContactRpcArgs({ listing, currentUser, subject, message, sourcePage });
  if (contactArgs.errors) return contactArgs;

  const { data, error } = await supabase
    .rpc("start_listing_contact", contactArgs.args)
    .single();
  throwOnError(error, "Start live listing contact");

  const [thread] = await loadLiveMessageThreads(supabase, {
    threadId: data.thread_id,
    currentUserId: currentUser?.database_id || currentUser?.id,
  });

  return {
    ...data,
    thread,
  };
}

export async function sendLiveThreadReply(client, { thread, currentUser, message } = {}) {
  const supabase = requireClient(client);
  const replyInsert = buildThreadReplyInsert({ thread, currentUser, message });
  if (replyInsert.errors) return replyInsert;

  const { data: messageRow, error: messageError } = await supabase
    .from("messages")
    .insert(replyInsert.row)
    .select("*")
    .single();
  throwOnError(messageError, "Send live message reply");

  const { error: threadError } = await supabase
    .from("message_threads")
    .update({ last_message_at: messageRow.created_at })
    .eq("id", replyInsert.row.thread_id);
  throwOnError(threadError, "Update live message thread activity");

  const [updatedThread] = await loadLiveMessageThreads(supabase, {
    threadId: replyInsert.row.thread_id,
    currentUserId: currentUser?.database_id || currentUser?.id,
  });

  return {
    message: messageRow,
    thread: updatedThread,
  };
}

export function createLiveMarketplaceService(client = getSupabaseBrowserClient()) {
  const configured = Boolean(client);

  return {
    configured,
    client,
    getSession: () => getLiveSession(client),
    subscribeAuth: (callback) => subscribeLiveAuth(client, callback),
    signIn: (input) => signInLiveUser(client, input),
    signUp: (input) => signUpLiveUser(client, input),
    signOut: () => signOutLiveUser(client),
    loadProfile: (authUser) => loadLiveUserProfile(client, authUser),
    saveProfile: (input) => saveLiveProfile(client, input),
    submitListing: (input) => submitLiveListing(client, input),
    loadReferenceData: () => loadLiveReferenceData(client),
    loadPublicListings: () => loadLivePublicListings(client),
    loadPendingListings: () => loadLivePendingListings(client),
    decideListing: (input) => decideLiveListing(client, input),
    startListingContact: (input) => startLiveListingContact(client, input),
    loadMessageThreads: (input) => loadLiveMessageThreads(client, input),
    sendThreadReply: (input) => sendLiveThreadReply(client, input),
    hydrateWindow: async () => {
      if (!configured) return { listings: [], users: [], categories: [], parishes: [] };
      const [referenceData, publicListings] = await Promise.all([
        loadLiveReferenceData(client),
        loadLivePublicListings(client),
      ]);
      upsertWindowRecords({
        ...referenceData,
        listings: publicListings.listings,
        users: publicListings.users,
        replace: true,
      });
      return { ...referenceData, ...publicListings };
    },
  };
}
